import {
  EntityLinks,
  Goal,
  ID,
  IDPrefixes,
  Task,
  Timestamp,
} from '@griply/types';
import {
  createPrefixId,
  getDirectGoalLifeAreaIds,
  getGoalParentIds,
} from '@griply/utils';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { stripUndefined } from '../utils/strip-undefined';

export type DuplicateGoalOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  goal: Goal;
  subGoals: Goal[];
  tasks: Task[];
  // Current relationship links; the copies get their own relationship docs resolved from these.
  links?: EntityLinks;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch: WriteBatchFn<NoInfer<TDocRef>, NoInfer<TDb>>;
  onWriteError: (error: unknown) => void;
};

const remapIds = (
  ids: ID[] | null | undefined,
  oldIdToNewRef: Map<ID, { id: ID }>,
  { keepUnmapped }: { keepUnmapped: boolean },
): ID[] | null => {
  if (!ids) {
    return null;
  }
  const remapped = ids
    .map((id) => oldIdToNewRef.get(id)?.id ?? (keepUnmapped ? id : undefined))
    .filter((id): id is ID => !!id);
  return remapped.length ? remapped : null;
};

export const duplicateGoal = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: DuplicateGoalOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<ID> => {
  const {
    goal,
    subGoals,
    tasks,
    links,
    collection,
    doc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
    onWriteError,
  } = options;

  const goalCollection = collection(db, FirestoreCollections.Goals);
  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const relationshipCollection = collection(
    db,
    FirestoreCollections.Relationships,
  );

  const sortedSubGoals = subGoals
    .slice()
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  const goalMap = new Map<ID, TDocRef & { id: ID }>();
  goalMap.set(goal.id, doc(goalCollection));
  sortedSubGoals.forEach(({ id }) => goalMap.set(id, doc(goalCollection)));

  const taskMap = new Map<ID, TDocRef & { id: ID }>();
  tasks.forEach((task) => taskMap.set(task.id, doc(taskCollection)));

  const batchDocuments = new BatchDocuments<TDocRef, TDb>(db, writeBatch);
  const dateBase = new Date();

  // Parents inside the copy point at their copies; parents outside it (and life areas) stay.
  const copiedLinks = new Map<ID, { parentIds: ID[]; lifeAreaIds: ID[] }>();
  [goal, ...sortedSubGoals].forEach((goalToCopy) => {
    copiedLinks.set(goalToCopy.id, {
      parentIds: getGoalParentIds(goalToCopy, { links }).map(
        (parentId) => goalMap.get(parentId)?.id ?? parentId,
      ),
      lifeAreaIds: getDirectGoalLifeAreaIds(goalToCopy, links),
    });
  });

  [goal, ...sortedSubGoals].forEach((goalToCopy, index) => {
    const docRef = goalMap.get(goalToCopy.id);
    if (!docRef) {
      return;
    }

    const { lifeAreaIds } = copiedLinks.get(goalToCopy.id) ?? {
      lifeAreaIds: [],
    };
    const newGoal: Goal = {
      ...goalToCopy,
      id: docRef.id,
      name:
        goal.id === goalToCopy.id
          ? `${goalToCopy.name} (copy)`
          : goalToCopy.name,
      image: null,
      // The copy's relationship doc below is its truth. The life-area scalar still trails it
      // for older clients; the parent chain does not — a migrated goal never mints one.
      // `ancestorIds` is the read-side name only: the stored key is cleared at the write below.
      parentLinksMigrated: true,
      lifeAreaLinksMigrated: true,
      lifeAreaId: lifeAreaIds[0] ?? null,
      ancestorIds: null,
      goalSorting: remapIds(goalToCopy.goalSorting, goalMap, {
        keepUnmapped: false,
      }),
      taskSorting: remapIds(goalToCopy.taskSorting, taskMap, {
        keepUnmapped: false,
      }),
      sections:
        goalToCopy.sections?.map((goalSection) => ({
          ...goalSection,
          id: doc(goalCollection).id,
          tasks:
            remapIds(goalSection.tasks, taskMap, { keepUnmapped: false }) ?? [],
        })) ?? [],
      completedTaskCount: 0,
      excludedFromLimits: null,
      frozenAt: null,
      createdAt: timestampFromDate(
        new Date(dateBase.setMilliseconds(index)),
      ) as unknown as Date,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      completedAt: null,
      roles: { owner: goalToCopy.roles.owner, all: [goalToCopy.roles.owner] },
    };

    batchDocuments.addDocument({
      ref: docRef,
      // `parentIds` is the stored spelling of `ancestorIds` and rides in on the spread above,
      // still naming the source's parents. Clearing it is what actually drops the legacy chain.
      data: stripUndefined({ ...newGoal, parentIds: null }),
    });
  });

  [goal, ...sortedSubGoals].forEach((goalToCopy) => {
    const docRef = goalMap.get(goalToCopy.id);
    const copied = copiedLinks.get(goalToCopy.id);
    if (!docRef || !copied) {
      return;
    }
    const id = createPrefixId(docRef.id, IDPrefixes.Goal);
    batchDocuments.addDocument({
      ref: doc(relationshipCollection, id),
      data: {
        id,
        references: [
          ...copied.parentIds.map((parentId) =>
            createPrefixId(parentId, IDPrefixes.Goal),
          ),
          ...copied.lifeAreaIds.map((lifeAreaId) =>
            createPrefixId(lifeAreaId, IDPrefixes.LifeArea),
          ),
        ],
        roles: { owner: goal.roles.owner, all: [goal.roles.owner] },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    });
  });

  tasks.forEach((task, index) => {
    const docRef = taskMap.get(task.id);
    if (!docRef) {
      return;
    }

    // Only goals inside the copy are kept; the derived index leads and the scalars trail it.
    const goalIds =
      remapIds(task.goalIds, goalMap, { keepUnmapped: false }) ?? [];
    const newTask: Task = {
      ...task,
      id: docRef.id,
      goalIds,
      lifeAreaIds: [],
      goalId: goalIds[0] ?? null,
      lifeAreaId: null,
      childIds: remapIds(task.childIds, taskMap, { keepUnmapped: false }),
      parentIds: remapIds(task.parentIds, taskMap, { keepUnmapped: false }),
      subtaskOrder: remapIds(task.subtaskOrder, taskMap, {
        keepUnmapped: false,
      }),
      createdAt: timestampFromDate(
        new Date(dateBase.setMilliseconds(index)),
      ) as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      completedAt: null,
      frozenAt: null,
      excludedFromLimits: null,
      roles: { owner: goal.roles.owner, all: [goal.roles.owner] },
    };

    batchDocuments.addDocument({ ref: docRef, data: stripUndefined(newTask) });
  });

  detachWrite(batchDocuments.commit(), onWriteError);

  const newRootRef = goalMap.get(goal.id);
  if (!newRootRef) {
    throw new Error('duplicateGoal: missing new root goal reference');
  }
  return newRootRef.id;
};
